import {
  ContextualMenu,
  DefaultButton,
  IDragOptions,
  Modal,
  Stack,
  Text,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { CloseButton } from 'common/components/Buttons';
import {
  FormHookAmount,
  FormHookDropdown,
  FormHookTextField,
} from 'common/components/FormHooksFields';
import { FormHookCheckBox } from 'common/components/FormHooksFields/FormHookCheckBox';
import { OnRenderRankLabel } from 'common/components/RankOptionUpdateComponent/OnRenderRankLabel';
import { OptionWithIcons } from 'common/components/RankOptionUpdateComponent/OptionWithIcons';
import { RankOptionDelete } from 'common/components/RankOptionUpdateComponent/RankOptionDelete';
import { rankOptionType } from 'common/components/RankOptionUpdateComponent/types';
import { useCommonStyles } from 'common/styles';
import { EntityDeleteInput } from 'common/types/globalTypes';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';
import { APPROVAL_BRANCH_INITIAL_VALUES } from 'settings/account/intuitiveApprovalSetupFlow/ApprovalFlowDesigner/constants';
import {
  ApprovalBranchViewData,
  ApprovalBranchViewRowValues,
} from 'settings/account/intuitiveApprovalSetupFlow/ApprovalFlowDesigner/types';
import { RankOptionViewModal } from '../../../../../../../../common/components/RankOptionUpdateComponent/RankOptionViewModal';
import { useStyles } from './index.styles';
import { useDropdownOptions } from './util';
import { validationFormSchema } from './valdation';

const OCCUPATION_TOOLTIP = (
  <>
    Select an occupation when the branch only applies to approvers with a
    certain occupation title.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const RANK_TOOLTIP = (
  <>
    Select a rank type when the branch only applies to approvers with a certain
    rank.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const BUSINESS_UNIT = (
  <>
    If checked, only approvers with access to the transaction’s business unit
    will be eligible to approve.
    <br />
    (Leave unchecked if no restrictions apply)
  </>
);

const USER_GROUP_TOOLTIP = (
  <>
    If checked, only approvers with access to the transaction’s user group will
    be eligible to approve.
    <br />
    (Leave unchecked if no restrictions apply)
  </>
);

const DEPARTMENT_TOOLTIP = (
  <>
    If checked, only approvers with access to the transaction’s department will
    be eligible to approve.
    <br />
    (Leave unchecked if no restrictions apply)
  </>
);

const PROJECT_TOOLTIP = (
  <>
    If checked, only approvers with access to the transaction’s project will be
    eligible to approve.
    <br />
    (Leave unchecked if no restrictions apply)
  </>
);

const WORKGROUP_TOOLTIP = (
  <>
    If checked, only approvers with access to the transaction’s workgroup will
    be eligible to approve.
    <br />
    (Leave unchecked if no restrictions apply)
  </>
);

const BUDGET_TOOLTIP = (
  <>
    If checked, the branch will be included when a transaction’s budget
    threshold matches either the Unused amount or percentage used values.
    <br />
    (Leave unchecked if no restrictions apply)
  </>
);

const REMAINING_TOOLTIP = (
  <>
    Enter the remaining budget amount (previous + transaction amount) used on
    the transaction’s department that will trigger a budgeting event.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const PERCENTAGE_TOOLTIP = (
  <>
    Enter the % of budget used (previous + transaction amount) used on the
    transaction’s department that will trigger a budgeting event.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const RANGE_TOOLTIP = (
  <>
    Enter the amount range that is required for this branch to be included.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const CONVERSION_TOOLTIP = (
  <>
    Enter a conversion option if the amount should be converted before
    evaluating the amount range.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const BUSINESS_UNIT_DROPDOWN_TOOLTIP = (
  <>
    Select a business unit when the transaction’s primary business unit matches
    this business unit, to include this approval branch.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const DEPARTMENT_DROPDOWN_TOOLTIP = (
  <>
    Select a department when the transaction’s primary department matches this
    department, to include this approval branch.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const DEPARTMENT_GROUP_DROPDOWN_TOOLTIP = (
  <>
    Select a department group if the transaction’s primary department is
    included in the chosen group, to ensure this approval branch is included.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const TRANSACTION_HINT_DROPDOWN_TOOLTIP = (
  <>
    Select a transaction hint when the transaction’s hint is required to include
    this approval branch.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const REQUESTER_DROPDOWN_TOOLTIP = (
  <>
    Select a user when the transaction’s requester matches this user, to include
    this approval branch.
    <br />
    (Leave blank if no restrictions apply)
  </>
);

const VALID_TOOLTIP = (
  <>
    If checked, the branch is included when the transaction’s values do not
    match any of the transactional requirements.
    <br />
    (Leave unchecked to ignore this (NOT) feature)
  </>
);

const TIMING_TOOLTIP = (
  <>
    If this approval flow allows delayed approvers, select the timing option
    that determines when additional approvers should be included.
    <br />
    (Leave blank if delayed approvers are not required)
  </>
);

const DAY_HOUR_TOOLTIP = (
  <>
    Enter the day/hour values required for the timing option.
    <br />
    (Leave blank if delayed approvers are not required)
  </>
);

const FORM_WIDTH_LAYOUT = 450;
const dragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
  dragHandleSelector: '.ms-Modal-scrollableContent > div:first-child',
};
export const FormView: React.FC<{
  data?: ApprovalBranchViewData;
  onClose: () => void;
  onSave: (formFields: any) => void;
  approvalTypes: ApprovalSetupFlowCommonData | undefined;
  visibility: boolean;
  defaultBranchName?: string | null;
  handleUserRankUpdate?: (newId: string) => void;
}> = ({
  onClose,
  approvalTypes,
  onSave,
  data,
  visibility,
  defaultBranchName,
  handleUserRankUpdate,
}) => {
  const theme = useTheme();
  const styles = useStyles();
  const isNew = data === undefined;
  const [openRankModal, setOpenRankModal] = useState<boolean>(false);
  const [rankDeleteDialog, setRankDeleteDialog] = useState(false);
  const [rankDeleteEntity, setRankDeleteEntity] = useState<
    EntityDeleteInput | undefined
  >(undefined);
  const [selectedOption, setSelectedOption] = useState<
    rankOptionType | undefined
  >(undefined);
  const {
    conversionOptions,
    occupations,
    ranks,
    additionalBusUnit,
    departments,
    departmentGroups,
    transactionTypesOptions,
    delayTypesOptions,
    userDistributionOptions,
  } = useDropdownOptions(approvalTypes);

  const formMethods = useForm<ApprovalBranchViewRowValues>({
    mode: 'all',
    resolver: yupResolver(validationFormSchema),
  });
  const {
    watch,
    handleSubmit,
    trigger,
    reset,
    setValue,
    formState: { isDirty, isValid },
  } = { ...formMethods };
  const currentuserRankId = watch('userRank.id');
  const osOverBudgetWatch = watch('isOverBudgetSelector');
  const commonStyles = useCommonStyles();
  const onHandleSave = async (values: ApprovalBranchViewRowValues) => {
    onSave(values);
  };
  useEffect(() => {
    reset(
      !isNew
        ? ({
            ...data,
          } as ApprovalBranchViewRowValues)
        : { ...APPROVAL_BRANCH_INITIAL_VALUES, description: defaultBranchName }
    );
    trigger();
  }, [data?.id, reset, trigger, isNew, visibility]);

  return (
    <FormProvider {...formMethods}>
      {visibility && (
        <Modal
          isOpen
          dragOptions={dragOptions}
          isModeless={!isValid && !isNew}
          styles={{
            scrollableContent: {
              overflow: 'scroll',
              '::-webkit-scrollbar': {
                width: 5,
              },
              '::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.neutralLight,
                borderRadius: '6px',
                width: 5,
              },
              '::-webkit-scrollbar-thumb:hover': {
                backgroundColor: theme.palette.neutralLighter,
              },
            },
          }}
          onDismiss={() => {
            if (isValid && (isNew || isDirty)) {
              handleSubmit(onHandleSave)();
            } else onClose();
          }}
        >
          {/* Header */}
          <Stack
            horizontal
            // grow={1}
            style={{
              position: 'fixed',
              backgroundColor: theme.palette.neutralLighterAlt,
              width: FORM_WIDTH_LAYOUT,
              zIndex: 10,
            }}
            horizontalAlign="space-between"
            tokens={{ padding: '20px 20px 20px 20px' }}
          >
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 7 }}
            >
              <Text variant={'xLarge'}>{`${isNew ? 'Add' : 'Edit'}`}</Text>

              <Text
                variant="xLarge"
                className={clsx(
                  commonStyles.colorThemePrimary,
                  commonStyles.bold
                )}
              >
                Branch
              </Text>
            </Stack>
            <Stack
              verticalAlign="center"
              horizontal
              tokens={{ childrenGap: 20 }}
            >
              <Text
                variant="xLarge"
                className={clsx(
                  commonStyles.colorThemePrimary,
                  commonStyles.bold
                )}
              >
                {/* @ts-ignore */}
                {data?.levelName}
              </Text>

              <CloseButton
                onClick={() => {
                  if (isValid && (isNew || isDirty)) {
                    handleSubmit(onHandleSave)();
                  } else onClose();
                }}
                disabled={!isValid && !isNew}
              />
            </Stack>
          </Stack>
          <Stack
            style={{
              // width: '100%',
              width: FORM_WIDTH_LAYOUT,
              maxHeight: 600,
              backgroundColor: `${theme.palette.neutralLighterAlt}66`,
            }}
          >
            <Stack tokens={{ childrenGap: 50, padding: '100px 20px' }}>
              <FormHookTextField
                label="Branch description"
                name={`description`}
                disabled={data?.isFlowUpdatable === false}
              />
              <Stack tokens={{ childrenGap: 10 }}>
                <Text
                  className={commonStyles.colorThemePrimary}
                  variant="large"
                >
                  Approver requirements
                </Text>
                <FormHookDropdown
                  options={occupations}
                  label="Occupation"
                  name={`userOccupation.id`}
                  disabled={data?.isFlowUpdatable === false}
                  toolTipText={OCCUPATION_TOOLTIP}
                />
                <Stack style={{ marginTop: 40 }}>
                  <FormHookDropdown
                    onRenderLabel={() => (
                      <OnRenderRankLabel
                        onIconClick={() => setOpenRankModal(true)}
                      />
                    )}
                    options={ranks}
                    name={`userRank.id`}
                    disabled={data?.isFlowUpdatable === false}
                    toolTipText={RANK_TOOLTIP}
                    onRenderOption={(option) =>
                      option ? (
                        <OptionWithIcons
                          option={option as rankOptionType}
                          setRankDeleteEntity={setRankDeleteEntity}
                          setRankDeleteDialog={setRankDeleteDialog}
                          setOpenRankModal={setOpenRankModal}
                          setSelectedOption={setSelectedOption}
                          currentuserRankId={currentuserRankId}
                        />
                      ) : null
                    }
                  />
                </Stack>
                {openRankModal && (
                  <RankOptionViewModal
                    onClose={() => {
                      setSelectedOption(undefined);
                      setOpenRankModal(false);
                    }}
                    option={selectedOption}
                    onCreate={(id) => {
                      setValue('userRank.id', id);
                      handleUserRankUpdate?.(id);
                    }}
                  />
                )}

                {rankDeleteEntity && rankDeleteDialog && (
                  <RankOptionDelete
                    rankDeleteEntity={rankDeleteEntity}
                    setRankDeleteDialog={setRankDeleteDialog}
                    onClose={() => {
                      setRankDeleteEntity(undefined);
                      setOpenRankModal(false);
                    }}
                  />
                )}
              </Stack>

              <Stack tokens={{ childrenGap: 20 }}>
                <Text
                  className={commonStyles.colorThemePrimary}
                  variant="large"
                >
                  Access requirements
                </Text>
                <Stack tokens={{ childrenGap: 16 }}>
                  <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Stack.Item className={commonStyles.flexThird}>
                      <TooltipHost
                        content={BUSINESS_UNIT}
                        id="businessUnitTooltip"
                      >
                        <FormHookCheckBox
                          className={styles.verticalCheckbox}
                          label="Business unit?"
                          boxSide="start"
                          name="isBusinessUnitSelector"
                          disabled={data?.isFlowUpdatable === false}
                        />
                      </TooltipHost>
                    </Stack.Item>

                    <Stack.Item className={commonStyles.flexThird}>
                      <TooltipHost
                        content={USER_GROUP_TOOLTIP}
                        id="userGroupTooltip"
                      >
                        <FormHookCheckBox
                          className={styles.verticalCheckbox}
                          label="User group?"
                          boxSide="start"
                          name="isUserGroupSelector"
                          disabled={data?.isFlowUpdatable === false}
                        />
                      </TooltipHost>
                    </Stack.Item>

                    <Stack.Item className={commonStyles.flexThird}>
                      <TooltipHost
                        content={DEPARTMENT_TOOLTIP}
                        id="departmentTooltip"
                      >
                        <FormHookCheckBox
                          className={styles.verticalCheckbox}
                          label="Department?"
                          boxSide="start"
                          name="isDepartmentSelector"
                          disabled={data?.isFlowUpdatable === false}
                        />
                      </TooltipHost>
                    </Stack.Item>
                  </Stack>

                  <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Stack.Item className={commonStyles.flexThird}>
                      <TooltipHost
                        content={PROJECT_TOOLTIP}
                        id="projectTooltip"
                      >
                        <FormHookCheckBox
                          className={styles.verticalCheckbox}
                          label="Project?"
                          boxSide="start"
                          name="isProjectSelector"
                          disabled={data?.isFlowUpdatable === false}
                        />
                      </TooltipHost>
                    </Stack.Item>

                    <Stack.Item className={commonStyles.flexThird}>
                      <TooltipHost
                        content={WORKGROUP_TOOLTIP}
                        id="workgroupTooltip"
                      >
                        <FormHookCheckBox
                          className={styles.verticalCheckbox}
                          label="Workgroup?"
                          boxSide="start"
                          name="isWorkgroupSelector"
                          disabled={data?.isFlowUpdatable === false}
                        />
                      </TooltipHost>
                    </Stack.Item>

                    <Stack.Item className={commonStyles.flexThird}>
                      <TooltipHost content={BUDGET_TOOLTIP} id="budgetTooltip">
                        <FormHookCheckBox
                          className={styles.verticalCheckbox}
                          label="Budget?"
                          boxSide="start"
                          name="isOverBudgetSelector"
                          disabled={data?.isFlowUpdatable === false}
                        />
                      </TooltipHost>
                    </Stack.Item>
                  </Stack>
                </Stack>
                {osOverBudgetWatch && (
                  <Stack
                    styles={{
                      root: {
                        backgroundColor: theme.palette.neutralLighter,
                        margin: -20,
                        padding: `10px 20px 20px 20px`,
                      },
                    }}
                    verticalAlign="center"
                    horizontal
                    tokens={{ childrenGap: 20 }}
                  >
                    <Stack.Item className={commonStyles.flexHalf}>
                      <TooltipHost
                        content={REMAINING_TOOLTIP}
                        id="remianingTooltip"
                      >
                        <FormHookAmount
                          label="Unused $"
                          placeholder="Enter unused amount"
                          name={`budgetLimitAmount`}
                          disabled={data?.isFlowUpdatable === false}
                        />
                      </TooltipHost>
                    </Stack.Item>
                    <Text>or</Text>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <TooltipHost
                        content={PERCENTAGE_TOOLTIP}
                        id="percentTooltip"
                      >
                        <FormHookAmount
                          label="% Used"
                          placeholder="Enter % used"
                          name={`budgetLimitPercent`}
                          disabled={data?.isFlowUpdatable === false}
                        />
                      </TooltipHost>
                    </Stack.Item>
                  </Stack>
                )}
              </Stack>

              <Stack tokens={{ childrenGap: 20 }}>
                <Text
                  className={commonStyles.colorThemePrimary}
                  variant="large"
                >
                  Transactional requirements
                </Text>
                <TooltipHost content={RANGE_TOOLTIP} id="rangeTooltip">
                  <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item className={commonStyles.flexHalf}>
                      <FormHookAmount
                        label="Amount Range:"
                        name={`transactionStartAmountTotal`}
                        placeholder="From"
                        disabled={data?.isFlowUpdatable === false}
                      />
                    </Stack.Item>

                    <Stack.Item
                      className={commonStyles.flexHalf}
                      style={{ paddingTop: 28 }}
                    >
                      <FormHookAmount
                        name={`transactionEndAmountTotal`}
                        placeholder="To"
                        disabled={data?.isFlowUpdatable === false}
                      />
                    </Stack.Item>
                  </Stack>
                </TooltipHost>

                <Stack horizontal tokens={{ childrenGap: 20 }}>
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookDropdown
                      name={`transactionAmountConversionTypeId`}
                      label="Conversion"
                      options={conversionOptions}
                      disabled={data?.isFlowUpdatable === false}
                      toolTipText={CONVERSION_TOOLTIP}
                    />
                  </Stack.Item>
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookDropdown
                      options={transactionTypesOptions}
                      label="Transaction Hint"
                      name={`approverTransactionTypeId`}
                      disabled={data?.isFlowUpdatable === false}
                      toolTipText={TRANSACTION_HINT_DROPDOWN_TOOLTIP}
                    />
                  </Stack.Item>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 20 }}>
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookDropdown
                      name={`transactionBusinessUnitId`}
                      label="Business Unit"
                      options={additionalBusUnit}
                      disabled={data?.isFlowUpdatable === false}
                      toolTipText={BUSINESS_UNIT_DROPDOWN_TOOLTIP}
                    />
                  </Stack.Item>
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookDropdown
                      options={departments}
                      label="Department"
                      name={`transactionDepartmentId`}
                      disabled={data?.isFlowUpdatable === false}
                      toolTipText={DEPARTMENT_DROPDOWN_TOOLTIP}
                    />
                  </Stack.Item>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookDropdown
                      name={`transactionDepartmentGroupId`}
                      label="Department Group"
                      options={departmentGroups}
                      disabled={data?.isFlowUpdatable === false}
                      toolTipText={DEPARTMENT_GROUP_DROPDOWN_TOOLTIP}
                    />
                  </Stack.Item>
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookDropdown
                      options={userDistributionOptions}
                      label="Requester"
                      name={`requesterUserId`}
                      disabled={data?.isFlowUpdatable === false}
                      toolTipText={REQUESTER_DROPDOWN_TOOLTIP}
                    />
                  </Stack.Item>
                </Stack>
              </Stack>

              <Stack.Item className={commonStyles.flexHalf}>
                <TooltipHost content={VALID_TOOLTIP} id="validtooltip">
                  <FormHookCheckBox
                    label="Valid when all transactional requirements are not met"
                    name="isTransactionNotCondition"
                    disabled={data?.isFlowUpdatable === false}
                  />
                </TooltipHost>
              </Stack.Item>

              <Stack tokens={{ childrenGap: 10 }}>
                <Text
                  className={commonStyles.colorThemePrimary}
                  variant="large"
                >
                  Delayed approver
                </Text>
                <Stack tokens={{ childrenGap: 20 }}>
                  <Stack
                    horizontal
                    tokens={{ childrenGap: 20 }}
                    verticalAlign="end"
                  >
                    <Stack.Item className={commonStyles.flexFull}>
                      <FormHookDropdown
                        options={delayTypesOptions}
                        label="Timing"
                        name={`approverDelayStatusTypeId`}
                        disabled={data?.isFlowUpdatable === false}
                        toolTipText={TIMING_TOOLTIP}
                      />
                    </Stack.Item>
                    <Stack.Item className={commonStyles.flexHalf} />
                  </Stack>
                  <TooltipHost content={DAY_HOUR_TOOLTIP}>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                      <Stack.Item className={commonStyles.flexHalf}>
                        <FormHookAmount
                          name={'approverDelayDays'}
                          label="Days"
                          disabled={data?.isFlowUpdatable === false}
                        />
                      </Stack.Item>
                      <Stack.Item className={commonStyles.flexHalf}>
                        <FormHookAmount
                          name={'approverDelayHours'}
                          label="Hours"
                          disabled={data?.isFlowUpdatable === false}
                        />
                      </Stack.Item>
                    </Stack>
                  </TooltipHost>
                </Stack>
              </Stack>
            </Stack>
            {/* Footer */}
            <Stack
              horizontal
              style={{
                position: 'fixed',
                backgroundColor: `${theme.palette.neutralLighterAlt}`,
                width: FORM_WIDTH_LAYOUT,
                zIndex: 10,
                bottom: 0,
              }}
              tokens={{ padding: '10px 20px 20px 20px', childrenGap: 10 }}
            >
              <DefaultButton
                text="Undo Changes"
                onClick={onClose}
                disabled={!isDirty && !isNew}
              />
              <Stack style={{ marginLeft: 'auto' }}>
                <DefaultButton
                  text="Close"
                  onClick={() => {
                    if (isValid && (isNew || isDirty)) {
                      handleSubmit(onHandleSave)();
                    } else onClose();
                  }}
                  disabled={!isValid && !isNew}
                />
              </Stack>
            </Stack>
          </Stack>
        </Modal>
      )}
    </FormProvider>
  );
};
